import React from 'react'
import { AlertTriangle, AlertCircle, RefreshCw, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { GateResult } from '@/types'

interface TimerLockBannerProps {
  gate: GateResult
  onRefresh: () => void
  isRefreshing: boolean
}

// severity → Tailwind class 對應表（版型沿用舊版兩個獨立鎖定橫幅元件的版型，
// red 沿用過期文件配色、amber 沿用必讀公告配色，orange 為個資未填新增的同結構配色）
const SEVERITY_CLASSES: Record<GateResult['severity'], {
  container: string
  iconWrap: string
  icon: string
  title: string
  summary: string
  refreshBtn: string
  description: string
  listWrap: string
  listDivider: string
  itemIcon: string
  badge: string
  footerNote: string
  cta: string
}> = {
  red: {
    container: 'bg-gradient-to-r from-red-50 to-red-100 border border-red-200',
    iconWrap: 'bg-red-500/10',
    icon: 'text-red-600',
    title: 'text-red-800',
    summary: 'text-red-600',
    refreshBtn: 'text-red-700 bg-white/70 hover:bg-white border border-red-200',
    description: 'text-red-700',
    listWrap: 'bg-white/60 border border-red-100',
    listDivider: 'border-red-100',
    itemIcon: 'text-red-500',
    badge: 'text-red-600 bg-red-100',
    footerNote: 'bg-red-50 text-red-600 border-red-100',
    cta: 'bg-red-500 hover:bg-red-600'
  },
  amber: {
    container: 'bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200',
    iconWrap: 'bg-amber-500/10',
    icon: 'text-amber-600',
    title: 'text-amber-800',
    summary: 'text-amber-600',
    refreshBtn: 'text-amber-700 bg-white/70 hover:bg-white border border-amber-200',
    description: 'text-amber-700',
    listWrap: 'bg-white/60 border border-amber-100',
    listDivider: 'border-amber-100',
    itemIcon: 'text-amber-500',
    badge: 'text-amber-600 bg-amber-100',
    footerNote: 'bg-amber-50 text-amber-600 border-amber-100',
    cta: 'bg-amber-500 hover:bg-amber-600'
  },
  orange: {
    container: 'bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200',
    iconWrap: 'bg-orange-500/10',
    icon: 'text-orange-600',
    title: 'text-orange-800',
    summary: 'text-orange-600',
    refreshBtn: 'text-orange-700 bg-white/70 hover:bg-white border border-orange-200',
    description: 'text-orange-700',
    listWrap: 'bg-white/60 border border-orange-100',
    listDivider: 'border-orange-100',
    itemIcon: 'text-orange-500',
    badge: 'text-orange-600 bg-orange-100',
    footerNote: 'bg-orange-50 text-orange-600 border-orange-100',
    cta: 'bg-orange-500 hover:bg-orange-600'
  }
}

const TimerLockBanner: React.FC<TimerLockBannerProps> = ({ gate, onRefresh, isRefreshing }) => {
  const navigate = useNavigate()
  // 後端先部署、前端還是舊版時可能收到沒見過的 severity。
  // 直接索引會 undefined 而整頁 crash，且只有「被鎖住的人」才會遇到，最難重現。
  const cls = SEVERITY_CLASSES[gate.severity] ?? SEVERITY_CLASSES.amber

  return (
    <div className={`${cls.container} rounded-2xl p-5 animate-fade-in`}>
      {/* 標題列 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${cls.iconWrap} flex items-center justify-center`}>
            <AlertTriangle className={`w-5 h-5 ${cls.icon}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${cls.title}`}>{gate.title}</h3>
            <p className={`text-sm ${cls.summary}`}>{gate.summary}</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
            transition-all disabled:opacity-50 ${cls.refreshBtn}`}
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          重新檢查
        </button>
      </div>

      {/* 說明文字 */}
      <p className={`text-sm mb-4 ${cls.description}`}>{gate.description}</p>

      {/* 項目列表 */}
      <div className={`rounded-xl overflow-hidden mb-4 ${cls.listWrap}`}>
        {gate.items.slice(0, 3).map((item, index) => (
          <div
            key={item.id}
            className={`flex items-center justify-between gap-3 px-4 py-3 ${
              index !== 0 ? `border-t ${cls.listDivider}` : ''
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <AlertCircle className={`w-4 h-4 flex-shrink-0 ${cls.itemIcon}`} />
              <span className="text-sm font-medium text-secondary-800 truncate">{item.label}</span>
            </div>
            {item.note && (
              <span className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${cls.badge}`}>
                {item.note}
              </span>
            )}
          </div>
        ))}
        {gate.totalCount > 3 && (
          <div className={`px-4 py-2 text-xs text-center border-t ${cls.footerNote}`}>
            還有 {gate.totalCount - 3} 項...
          </div>
        )}
      </div>

      {/* CTA 按鈕 */}
      <button
        onClick={() => navigate(gate.ctaPath)}
        className={`w-full flex items-center justify-center gap-2 py-3 px-4
          text-white font-medium rounded-xl transition-all shadow-sm hover:shadow-md ${cls.cta}`}
      >
        {gate.ctaText}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  )
}

export default TimerLockBanner
